import { useCallback, useState } from 'react';
import { useNotification } from '@refinedev/core';
import { httpClient, ROOT_URL } from '../../App';
import { getErrorMessage } from '../../api/error';
import { OnboardingStatus, WorldcheckVerification } from './useWorldCheckVerification.hook';

interface UpdateCustomer {
  fullName?: string;
  smartUID?: string;
  address?: string;
  website?: string | null;
  worldCheckVerification?: WorldcheckVerification;
  onboardingStatus?: OnboardingStatus;
}

export const useUpdateCustomer = (id: number | undefined) => {
  const [isLoading, setIsLoading] = useState(false);
  const { open: openNotification } = useNotification();

  const update = useCallback(
    async (data: UpdateCustomer) => {
      if (!Number.isInteger(id)) {
        return null;
      }
      setIsLoading(true);
      try {
        await httpClient.patch(`${ROOT_URL}/api/v1/users/${id}`, data);
        setIsLoading(false);
        return true;
      } catch (error) {
        const message = getErrorMessage(error);
        if (openNotification) {
          openNotification({
            key: 'edit_failure',
            message: `Error editing customer #${id}`,
            description: message || 'Something went wrong',
            type: 'error',
          });
        }
        setIsLoading(false);
        return false;
      }
    },
    [id, openNotification],
  );

  return { update, isLoading };
};

export const useCreateCustomer = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { open: openNotification } = useNotification();

  const add = useCallback(
    async (data: UpdateCustomer) => {
      setIsLoading(true);
      try {
        await httpClient.post(`${ROOT_URL}/api/v1/users/create`, data);
        setIsLoading(false);
        return true;
      } catch (error) {
        const message = getErrorMessage(error);
        if (openNotification) {
          openNotification({
            key: 'create_failure',
            message: `Error creating customer`,
            description: message || 'Something went wrong',
            type: 'error',
          });
        }
        setIsLoading(false);
        return false;
      }
    },
    [openNotification],
  );

  return { add, isLoading };
};
